import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, LogOut } from "lucide-react";
import { SEO } from "@/components/SEO";
import { IQAdminLogin } from "@/components/iqAdmin/IQAdminLogin";
import { IQAdminStats } from "@/components/iqAdmin/IQAdminStats";
import { IQAdminTable } from "@/components/iqAdmin/IQAdminTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "iq_admin_token";

export default function IQAdmin() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [email, setEmail] = useState("");
  const [checking, setChecking] = useState(true);
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ total: 0, items: [] });
  const [filters, setFilters] = useState({});

  const authHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setEmail("");
  };

  const login = async (loginEmail, password) => {
    const { data } = await axios.post(`${API}/iq/admin/login`, { email: loginEmail, password });
    localStorage.setItem(STORAGE_KEY, data.token);
    setToken(data.token);
    setEmail(data.email);
  };

  // Verify any stored token on mount.
  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/iq/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setEmail(r.data.email))
      .catch(() => logout())
      .finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    const params = {
      product: filters.product || "",
      usage: filters.usage || "",
      frustration: filters.frustration || "",
      feature: filters.feature || "",
      device: filters.device || "",
      eval_interest: filters.eval_interest || "",
      source: filters.source || "",
      search: filters.search || "",
    };
    const [regRes, statsRes] = await Promise.all([
      axios.get(`${API}/iq/admin/registrations`, { ...authHeaders(), params }),
      axios.get(`${API}/iq/admin/stats`, authHeaders()),
    ]);
    setData(regRes.data);
    setStats(statsRes.data);
  }, [token, filters, authHeaders]);

  useEffect(() => {
    if (token && email) fetchData();
  }, [token, email, fetchData]);

  const exportCsv = async () => {
    const params = {
      product: filters.product || "", usage: filters.usage || "", frustration: filters.frustration || "",
      feature: filters.feature || "", device: filters.device || "", eval_interest: filters.eval_interest || "",
      source: filters.source || "", search: filters.search || "",
    };
    const res = await axios.get(`${API}/iq/admin/registrations/export`, { ...authHeaders(), params, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = "wearspray-iq-registrations.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  if (checking) return <div className="min-h-screen bg-[#0a0a0a]" />;
  if (!token || !email) return <IQAdminLogin onLogin={login} />;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="iq-admin-page">
      <SEO title="WearSpray IQ Admin" description="Internal registration dashboard." path="/iq/admin" noindex />
      <div className="flex items-center justify-between px-6 md:px-10 h-16 md:h-20 border-b border-zinc-900">
        <Link to="/" className="flex items-center gap-2.5 group">
          <ArrowLeft size={15} className="text-zinc-500 group-hover:text-[#ff2d78] transition-colors" />
          <span className="font-heading text-sm font-medium tracking-tight text-zinc-400 group-hover:text-white transition-colors">
            Wear<span className="text-[#ff2d78]">Spray</span> IQ Admin
          </span>
        </Link>
        <button data-testid="iq-admin-logout" onClick={logout} className="inline-flex items-center gap-1.5 font-body text-xs text-zinc-400 hover:text-[#ff2d78] transition-colors">
          <LogOut size={13} /> {email}
        </button>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <IQAdminStats stats={stats} />
        <IQAdminTable items={data.items} total={data.total} filters={filters} setFilters={setFilters} onExport={exportCsv} />
      </div>
    </div>
  );
}
